import {
	addDays,
	addHours,
	addMinutes,
	addWeeks,
	addYears,
	differenceInDays,
	differenceInHours,
	differenceInMinutes,
	differenceInMonths,
	differenceInSeconds,
	differenceInYears,
	eachDayOfInterval,
	endOfDay,
	endOfWeek,
	getHours,
	isAfter,
	isBefore,
	isEqual,
	isSameDay,
	isSunday,
	isWithinInterval,
	parseISO,
	startOfDay,
	startOfWeek,
	subDays,
	subHours,
	subMinutes,
	subMonths,
	subYears,
} from 'date-fns';
import { type Holidays } from 'infra/repositories/contract/types';
import { formatDate } from 'infra/services/formatter';
import {
	type GetPXTime,
	type IntervalTwoDates,
	type IsDateBetweenTwoDates,
	type WeekOnRange,
} from './types';

export const ONE_SECOND = 1000;
export const ONE_MINUTE = ONE_SECOND * 60;
export const ONE_HOUR = ONE_MINUTE * 60;
export const ONE_DAY = ONE_HOUR * 24;

export const getHoursBetweenTwoDates = (start: Date, end: Date) => {
	return (end.getTime() - start.getTime()) / (1000 * 60 * 60);
};

const HOUR_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/;

export const isValidHour = (value: string) => HOUR_REGEX.test(value);

export const combineDateAndHour = (date: Date, hour: string): Date => {
	const [hours, minutes] = hour.split(':').map(Number);
	const combined = new Date(date);
	combined.setHours(hours, minutes, 0, 0);
	return combined;
};

export const getShiftDurationMinutes = (startHour: string, endHour: string) => {
	const toMinutes = (value: string) => {
		const [hours, minutes] = value.split(':').map(Number);
		return hours * 60 + minutes;
	};
	const totalDayMinutes = 24 * 60;
	const startMinutes = toMinutes(startHour);
	const endMinutes = toMinutes(endHour);

	if (endMinutes >= startMinutes) return endMinutes - startMinutes;
	return totalDayMinutes - startMinutes + endMinutes;
};

export const getTimeRemainingText = (
	startAt: string | Date,
	now = new Date(),
): string => {
	const diffHours = getHoursBetweenTwoDates(now, new Date(startAt));

	if (diffHours < 1) {
		return `${Math.floor(diffHours * 60)}m`;
	}

	return `${Math.floor(diffHours)}h`;
};

export const getDifferenceInYears = (
	start: Date | string,
	end: Date | string,
): number => {
	return differenceInYears(new Date(start), new Date(end));
};

export const checkDiffBetweenTwoDates = (min: Date, max: Date) => {
	return max.getTime() - min.getTime();
};

export const setDateAsEndOfDay = (date: Date) => {
	date.setHours(23);
	date.setMinutes(59);
	date.setSeconds(59);
	return date;
};

export const setDateAsStartOfDay = (date: Date) => {
	date.setHours(0);
	date.setMinutes(0);
	date.setSeconds(0);
	return date;
};

export const getSundaysBetweenTwoDates = ({
	startDate,
	endDate,
}: IntervalTwoDates) => {
	if (!startDate || !endDate) return [];
	let date = startDate;
	const sundays = [];
	while (isBefore(date, endDate)) {
		if (isSunday(date)) {
			sundays.push(date);
		}
		date = addDays(date, 1);
	}
	return sundays;
};

export const getWeeksOnRange = ({ startDate, endDate }: IntervalTwoDates) => {
	if (!startDate || !endDate) return [];
	const weeks = [] as WeekOnRange[];
	let currentWeek = startOfWeek(startDate, { weekStartsOn: 1 });
	while (isBefore(currentWeek, endDate)) {
		const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });
		if (isAfter(weekEnd, endDate) && isBefore(currentWeek, startDate))
			return [
				{
					start: startOfDay(currentWeek),
					end: startOfDay(weekEnd),
					selected: false,
					dateSelected: [],
				},
			];
		weeks.push({
			start: startOfDay(currentWeek),
			end: startOfDay(weekEnd),
			selected: false,
			dateSelected: [],
		});
		currentWeek = addWeeks(currentWeek, 1);
	}
	return weeks;
};

export const isDateInList = (date: Date, list: Date[]) => {
	return list.some((freeDate) => isSameDay(freeDate, startOfDay(date)));
};

export const isDateInListOrSet = (
	date: Date,
	list: Date[],
	set: Set<string>,
) => {
	return isDateInList(date, list) || set.has(formatDate(date, 'yyyy-MM-dd'));
};

export const isDateInHolidays = (date: Date, holidays: Holidays[]) => {
	if (!holidays?.length) return false;
	return holidays.some((item) =>
		isSameDay(parseISO(item.date), startOfDay(date)),
	);
};

export const getAllDateInRange = ({ startDate, endDate }: IntervalTwoDates) => {
	if (isAfter(startDate, endDate)) return [];
	return eachDayOfInterval({
		start: startOfDay(startDate),
		end: endOfDay(endDate),
	});
};

export const isDateBetweenTwoDatesWithoutHours = ({
	date,
	endDate,
	startDate,
}: IsDateBetweenTwoDates) => {
	const value = typeof date === 'string' ? parseISO(date) : date;
	const start = parseISO(startDate);
	const end = parseISO(endDate);
	const dateValue = setDateAsStartOfDay(value);
	const dateStart = setDateAsStartOfDay(start);
	const dateEnd = setDateAsStartOfDay(end);

	return isBefore(dateValue, dateStart) || isAfter(dateValue, dateEnd)
		? false
		: true;
};

export const deconstructDateByMilliseconds = (milliseconds: number) => {
	const leftDate = new Date(milliseconds);
	const rightDate = new Date(0);

	const years = differenceInYears(leftDate, rightDate);

	const months = differenceInMonths(subYears(leftDate, years), rightDate);

	const days = differenceInDays(
		subMonths(subYears(leftDate, years), months),
		rightDate,
	);

	const hours = differenceInHours(
		subDays(subMonths(subYears(leftDate, years), months), days),
		rightDate,
	);

	const minutes = differenceInMinutes(
		subHours(
			subDays(subMonths(subYears(leftDate, years), months), days),
			hours,
		),
		rightDate,
	);

	const seconds = differenceInSeconds(
		subMinutes(
			subHours(
				subDays(subMonths(subYears(leftDate, years), months), days),
				hours,
			),
			minutes,
		),
		rightDate,
	);

	return {
		years,
		months,
		days,
		hours,
		minutes,
		seconds,
	};
};

export const getPXTime = ({ pxTime, years, months, days }: GetPXTime) => {
	if (!pxTime) return '';
	let timeFormatted = '';

	if (pxTime?.years) {
		timeFormatted += years;
	}

	if (pxTime?.months) {
		timeFormatted += months;
	}

	if (pxTime?.days) {
		timeFormatted += days;
	}

	return timeFormatted.trim().replace(/(?:,)?\s*(?:and|e|y)$/, '');
};

export {
	addDays,
	addHours,
	addMinutes,
	addYears,
	getHours,
	isEqual,
	parseISO,
	startOfDay,
	subDays,
	isWithinInterval,
	isAfter,
	isBefore,
	isSameDay,
	isSunday,
	differenceInDays,
	endOfDay,
};
